import { CookieResponse } from '@/components/cookie-dialog';
import { useActiveCircle } from '@/hooks/use-active-circle';
import useDataWarehouseEvent from '@/hooks/use-data-warehouse-event';
import { logger, initDatadogLogs } from '@/libs/logger';
import { routes } from '@/utils/routes';
import {
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EnumStepName,
} from '@calm/evexia-analytics';
import { useCookieResponse } from '@rhg/hooks/use-cookie-response';
import useAuth from '@rhg/libs/auth0/use-auth';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { JSONValue } from '../../logger/types';
import { useAnalyticsBaseContext } from '../base-context-provider';
import {
    AnalyticsContext,
    AnalyticsState,
} from '../contexts/analytics-context';
import { AnalyticsDestination } from '../destination/types';
import trackPageNav, { getFeatureName, getScreenName } from '../track-page-nav';
import { useGatedDispatcher } from '../use-gated-dispatcher';

const TrackRouterPathChange = ({
    screenName,
}: {
    screenName: EnumScreenName;
}) => {
    const router = useRouter();
    const { trackDataWarehouseEvent } = useDataWarehouseEvent();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const { isLoading: isActiveCircleLoading } = useActiveCircle();

    useEffect(() => {
        if (
            !isAuthLoading &&
            isAuthenticated &&
            !isActiveCircleLoading &&
            router?.pathname &&
            router.isReady
        ) {
            trackDataWarehouseEvent({
                screenName,
                actionName: 'screen_viewed',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isAuthLoading,
        isAuthenticated,
        isActiveCircleLoading,
        router.isReady,
        screenName,
        router?.pathname,
    ]);

    return null;
};

interface AnalyticsProviderProps {
    children: ReactNode;
    destinations: AnalyticsDestination[];
}
export const AnalyticsProvider = ({
    children,
    destinations,
}: AnalyticsProviderProps) => {
    const areDestinationsInitialized = useRef(false);
    const {
        isResponseRequired,
        isFetched: isResponseFetched,
        response,
        deviceCountry,
    } = useCookieResponse();
    const router = useRouter();
    const { isAuthenticated } = useAuth();
    const { baseProperties, isReady } = useAnalyticsBaseContext();

    const [currentStep, setCurrentStep] = useState<EnumStepName | undefined>(
        undefined
    );
    const initialScreenName = getScreenName(router, baseProperties);
    const [currentScreenName, setCurrentScreenName] =
        useState<EnumScreenName>(initialScreenName);
    const [currentFeature, setCurrentFeature] = useState<EnumFeature>(
        getFeatureName(router, baseProperties)
    );
    const [openDialogs, setOpenDialogs] = useState<EnumDialogName[]>([]);

    const currentDialog = useMemo(
        () =>
            openDialogs.length > 0
                ? openDialogs[openDialogs.length - 1]
                : undefined,
        [openDialogs]
    );

    const state = useMemo(
        () =>
            ({
                dialog: {
                    currentDialog,
                    onDialogOpen: (dialogName: EnumDialogName) =>
                        setOpenDialogs([...openDialogs, dialogName]),
                    onDialogClose: (dialogName: EnumDialogName) =>
                        setOpenDialogs(
                            openDialogs.filter(
                                (openName) => openName !== dialogName
                            )
                        ),
                },
                wizard: {
                    step: currentStep,
                    setStep: (step?: EnumStepName) => setCurrentStep(step),
                },
                currentFeature,
                currentScreenName,
            } as AnalyticsState),
        // TODO: fix this eslint-disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            currentDialog,
            currentFeature,
            currentScreenName,
            currentStep,
            isAuthenticated,
            openDialogs,
            router.pathname,
        ]
    );

    useEffect(() => {
        initDatadogLogs();
    }, []);

    // Analytics should not be able to track until they are initialized. For Amplitude, the events are cached until we initialize
    useEffect(() => {
        if (
            (isResponseFetched && !isResponseRequired) ||
            response === CookieResponse.All
        ) {
            if (!areDestinationsInitialized.current) {
                areDestinationsInitialized.current = true;
                destinations.forEach((dest) => {
                    dest.init().catch(() => {});
                });
                destinations.forEach((dest) => {
                    dest.setUserProperties({
                        UserDeviceCountry: deviceCountry?.toUpperCase(),
                    }).catch(() => {});
                });
            }
        }
    }, [
        destinations,
        deviceCountry,
        isResponseFetched,
        isResponseRequired,
        response,
    ]);

    const screenName = getScreenName(router, baseProperties);
    const featureName = getFeatureName(router, baseProperties);

    const routePageNavDispatch = useCallback(
        (args: { pathname: string; query: ParsedUrlQuery }) =>
            trackPageNav({
                pathname: args.pathname,
                query: args.query,
                destinations,
                baseProperties,
            }),
        [destinations, baseProperties]
    );

    const gatedRoutePageNav = useGatedDispatcher(
        routePageNavDispatch,
        router.isReady && isReady
    );

    useEffect(() => {
        if (!router.isReady || !router.pathname) return;
        /*
         ** When navigating to the invite page with an id query param, the
         ** ResendInvite/Invite dialog handles its own screen tracking. Skip
         ** route-level tracking to prevent overriding currentScreenName after
         ** child component mount effects have already fired.
         */
        if (
            screenName === EnumScreenName.DependentInviteManage &&
            router.query.id
        )
            return;
        /*
         ** ConciergeCarePlan & Dynamic Home handle their own Screen : Viewed events
         ** to include concierge goal tracking props & dynamic home section counts, respectively.
         */
        if (
            screenName === EnumScreenName.ConciergeCarePlan ||
            screenName === EnumScreenName.Home ||
            // The emotions check-in flow fires per-step Screen : Viewed events
            // with emotion props from its provider.
            screenName === EnumScreenName.EmotionSelector ||
            screenName === EnumScreenName.EmotionalCheckinResults ||
            screenName === EnumScreenName.OverallEmotionalCheckinHistory ||
            screenName === EnumScreenName.EmotionalCheckinHistory ||
            screenName === EnumScreenName.SpecificEmotionalCheckinDetails ||
            // Your Playlists fires its own Screen : Viewed once playlists
            // load, with TotalPlaylists/PlaylistIdList.
            screenName === EnumScreenName.YourPlaylists
        )
            return;
        /*
         ** The feed detail page fires its own FeedDetails Screen : Viewed with
         ** feed properties + SourceScreenName. Skip the route-level fire so
         ** currentScreenName isn't overwritten before the page reads its inbound
         ** source screen. (The route is intentionally absent from the pathname
         ** map, so match on pathname rather than screenName.)
         */
        if (router.pathname === routes.discoverFeed('[feedId]')) return;
        /*
         ** The pack detail page fires its own PackDetails Screen : Viewed with
         ** PackClass/RowIndex/PackDisplayType + SourceScreenName. Skip the
         ** route-level fire so currentScreenName isn't overwritten before the
         ** page reads its inbound source screen. (Matches the analogous
         ** discoverFeed guard above.)
         */
        if (router.pathname === routes.discoverPack('[packClass]')) return;
        setCurrentFeature(featureName);
        setCurrentScreenName(screenName);
        gatedRoutePageNav({
            pathname: router.pathname,
            query: router.query,
        }).catch((error: Error) => {
            logger.error(
                'Error tracking page navigation',
                { pathname: router.pathname, query: router.query as JSONValue },
                error
            );
        });
    }, [
        router.isReady,
        router.pathname,
        router.query,
        screenName,
        featureName,
        gatedRoutePageNav,
    ]);

    return (
        <AnalyticsContext.Provider
            value={{
                destinations,
                state,
                setCurrentFeature,
                setCurrentScreenName,
            }}
        >
            <TrackRouterPathChange screenName={screenName} />
            {children}
        </AnalyticsContext.Provider>
    );
};
